import { useEffect, useRef, useState } from "react";
import { notificationsApi } from "@/lib/api";
import { Bell, BellRinging, Trash, X } from "@phosphor-icons/react";
import { formatDistanceToNowStrict } from "date-fns";

/**
 * Notifications bell — header-style component with a dropdown panel.
 *
 *   - Shows a red dot + count when there are unread notifications.
 *   - Polls every 60s for new ones (cheap because the unread-count
 *     endpoint just hits a counter).
 *   - Click → opens a dropdown that lists the latest 50 notifications.
 *   - Each row has a delete button. There's also a "Clear all" footer.
 *   - Notifications are auto-marked-read the moment the dropdown opens.
 *
 * Placement: top-right corner of MemberDashboard (and AdminDashboard later).
 */
export default function NotificationsBell({ onDark = false }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const refreshCount = () => {
    notificationsApi.unreadCount().then(({ data }) => setUnread(data?.count || 0)).catch(() => {});
  };

  const loadList = () => {
    notificationsApi.list().then(({ data }) => setList(data || [])).catch(() => setList([]));
  };

  useEffect(() => {
    refreshCount();
    const i = setInterval(refreshCount, 60000);
    return () => clearInterval(i);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      loadList();
      // Mark all read once the panel is opened — matches Gmail / Slack UX
      notificationsApi.markRead().then(() => setUnread(0)).catch(() => {});
    }
  };

  const remove = async (id, e) => {
    e?.stopPropagation();
    try {
      await notificationsApi.remove(id);
      setList((cur) => cur.filter((n) => n.id !== id));
    } catch (_) {}
  };

  const clearAll = async () => {
    try {
      await notificationsApi.clearAll();
      setList([]);
      setUnread(0);
    } catch (_) {}
  };

  const Icon = unread > 0 ? BellRinging : Bell;
  const triggerCls = onDark
    ? "p-2 rounded-xl text-white hover:bg-white/10"
    : "p-2 rounded-xl text-foreground hover:bg-secondary";

  return (
    <div className="relative" ref={ref} data-testid="notifications-bell">
      <button
        onClick={toggle}
        data-testid="notifications-bell-btn"
        className={`relative ${triggerCls} active:scale-95 transition-transform`}
        aria-label="Notifications"
      >
        <Icon className="w-5 h-5" weight={unread > 0 ? "fill" : "regular"} />
        {unread > 0 && (
          <span
            className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold leading-[18px] text-center"
            data-testid="notifications-unread-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 sm:w-96 max-h-[28rem] bg-card border border-border rounded-2xl shadow-xl z-50 overflow-hidden flex flex-col"
          data-testid="notifications-panel"
        >
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <p className="font-semibold text-sm">Notifications</p>
            {list.length > 0 && (
              <button
                onClick={clearAll}
                data-testid="notifications-clear-all-btn"
                className="text-xs text-muted-foreground hover:text-red-600"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-border">
            {list.length === 0 ? (
              <div className="px-6 py-12 text-center" data-testid="notifications-empty">
                <Bell className="w-10 h-10 text-muted-foreground mx-auto mb-2" weight="duotone" />
                <p className="text-sm text-muted-foreground">No notifications</p>
              </div>
            ) : (
              list.map((n) => (
                <div
                  key={n.id}
                  className="px-4 py-3 flex items-start gap-3 group"
                  data-testid={`notification-${n.id}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Bell className="w-4 h-4 text-primary" weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm leading-snug">{n.message}</p>
                    {n.createdAt && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {formatDistanceToNowStrict(new Date(n.createdAt), { addSuffix: true })}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={(e) => remove(n.id, e)}
                    data-testid={`notification-delete-${n.id}`}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600"
                    aria-label="Delete notification"
                  >
                    <Trash className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
          {list.length > 0 && (
            <div className="px-4 py-2 border-t border-border bg-secondary/30">
              <p className="text-[10px] text-muted-foreground text-center">
                Cleared notifications are archived and only accessible to the super admin.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
